import { useState } from 'react';
import { strategyOverview } from '../data';

interface TimelineNode {
  id: string;
  x: number; // horizontal position %
  time: string;
  entry: number; // entry-cost share % (for the stream boundary)
}

const timelineNodes: TimelineNode[] = [
  { id: 'scalping', x: 12.5, time: 'Seconds', entry: 100 },
  { id: 'day-trading', x: 37.5, time: 'Hours', entry: 94 },
  { id: 'swing-trading', x: 62.5, time: 'Days', entry: 48 },
  { id: 'position-trading', x: 87.5, time: 'Months', entry: 26 },
];

// Boundary line between the "entry cost" stream (top) and the "holding cost" stream (bottom).
// Values are % from the top of the chart, following the crossover from left to right.
const ENTRY_CLIP =
  'polygon(0 0, 100% 0, 100% 26%, 87.5% 26%, 62.5% 48%, 37.5% 94%, 12.5% 100%, 0 100%)';
const HOLDING_CLIP =
  'polygon(0 100%, 12.5% 100%, 37.5% 94%, 62.5% 48%, 87.5% 26%, 100% 26%, 100% 100%)';

export default function StrategyOverviewSection() {
  const [selectedId, setSelectedId] = useState<string>('scalping');
  const selected = strategyOverview.find((s) => s.id === selectedId)!;
  const selectedX = timelineNodes.find((n) => n.id === selectedId)!.x;

  return (
    <section className="relative w-full bg-background-50 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-14 md:py-20">
        {/* Header */}
        <div className="mb-10 md:mb-14 max-w-[560px]">
          <h2 className="text-[34px] md:text-[46px] font-bold text-foreground-950 tracking-[-0.02em] leading-[1.05]">
            Time moves the cost.
          </h2>
          <p className="mt-5 text-[15px] text-foreground-600 leading-relaxed">
            Hold for seconds and you fight the spread. Hold for months and swap quietly
            takes over. Every strategy sits somewhere on this same curve.
          </p>
        </div>

        {/* Stream legend */}
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-6">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-accent-500"></span>
            <span className="text-[12px] font-semibold text-foreground-700">Entry costs — spread + commission</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-primary-500"></span>
            <span className="text-[12px] font-semibold text-foreground-700">Holding costs — overnight swap</span>
          </div>
        </div>

        {/* The cost-migration stream */}
        <div className="relative">
          {/* Chart area */}
          <div className="relative w-full h-[220px] md:h-[300px] rounded-lg overflow-hidden bg-background-100">
            {/* Entry stream (top) */}
            <div
              className="absolute inset-0 bg-accent-500"
              style={{ clipPath: ENTRY_CLIP }}
            >
              <span className="absolute top-5 left-5 text-[11px] font-bold uppercase tracking-wider text-background-50">
                Entry costs
              </span>
            </div>
            {/* Holding stream (bottom) */}
            <div
              className="absolute inset-0 bg-primary-500"
              style={{ clipPath: HOLDING_CLIP }}
            >
              <span className="absolute bottom-5 right-5 text-[11px] font-bold uppercase tracking-wider text-background-50">
                Holding costs
              </span>
            </div>

            {/* Selected marker line */}
            <div
              className="absolute top-0 bottom-0 w-[2px] bg-foreground-950/30 transition-all duration-300"
              style={{ left: `${selectedX}%` }}
            ></div>

            {/* Node hotspots */}
            {timelineNodes.map((node) => {
              const active = node.id === selectedId;
              return (
                <button
                  key={node.id}
                  onClick={() => setSelectedId(node.id)}
                  className="absolute top-0 bottom-0 flex items-center justify-center cursor-pointer group"
                  style={{ left: `${node.x}%`, transform: 'translateX(-50%)', width: '56px' }}
                  aria-label={strategyOverview.find((s) => s.id === node.id)!.name}
                >
                  <span
                    className={`w-4 h-4 rounded-full border-2 border-background-50 transition-all duration-200 ${
                      active
                        ? 'bg-foreground-950 scale-125'
                        : 'bg-foreground-400 group-hover:bg-foreground-700'
                    }`}
                  ></span>
                </button>
              );
            })}
          </div>

          {/* Timeline axis with strategy labels */}
          <div className="relative w-full h-16 mt-1">
            {timelineNodes.map((node) => {
              const strategy = strategyOverview.find((s) => s.id === node.id)!;
              const active = node.id === selectedId;
              return (
                <button
                  key={node.id}
                  onClick={() => setSelectedId(node.id)}
                  className="absolute top-0 flex flex-col items-center cursor-pointer text-center px-1"
                  style={{ left: `${node.x}%`, transform: 'translateX(-50%)', width: '110px' }}
                >
                  <span
                    className={`text-[13px] md:text-[15px] font-bold transition-colors duration-200 leading-tight ${
                      active ? 'text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {strategy.name}
                  </span>
                  <span className="text-[11px] text-foreground-400 mt-0.5">{node.time}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected strategy detail — one narrative block that swaps */}
        <div className="mt-12 md:mt-14 border-t border-background-200 pt-10 md:pt-12">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Left: identity + narrative */}
            <div className="flex-1">
              <div className="flex items-baseline gap-3 flex-wrap mb-4">
                <h3 className="text-[26px] md:text-[30px] font-bold text-foreground-950 tracking-[-0.01em]">
                  {selected.name}
                </h3>
                <span className="text-[13px] text-foreground-500">{selected.tagline}</span>
              </div>
              <p className="text-[15px] text-foreground-600 leading-[1.7] max-w-[620px]">
                {selected.desc}
              </p>
            </div>

            {/* Right: at-a-glance facts */}
            <div className="lg:w-[300px] flex-shrink-0 space-y-4">
              <div className="flex items-baseline justify-between border-b border-background-200/70 pb-3">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-foreground-500">Hold time</span>
                <span className="text-[14px] font-semibold text-foreground-900">{selected.holdTime}</span>
              </div>
              <div className="flex items-baseline justify-between border-b border-background-200/70 pb-3">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-foreground-500">Frequency</span>
                <span className="text-[14px] font-semibold text-foreground-900">{selected.tradesPerDay} / day</span>
              </div>
              <div className="flex items-baseline justify-between border-b border-background-200/70 pb-3">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-foreground-500">Target</span>
                <span className="text-[14px] font-semibold text-foreground-900">{selected.targetPipsPerTrade}</span>
              </div>
              <div className="pt-1">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-foreground-500 block mb-1">
                  Dominant cost
                </span>
                <span className="text-[18px] font-bold text-primary-600">{selected.dominantCost}</span>
                <p className="text-[13px] text-foreground-500 mt-1">{selected.dominantCostLabel}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}